/**
 * WeatherChip — tiny inline weather pill.
 *
 * Two flavors:
 *   - `variant="card"` (default): small chip suitable for a truck card,
 *     shows icon + temperature. Adds rain probability when ≥40% so the
 *     user only sees the umbrella when it actually means something
 *     (e.g. "☀️ 72° · ☔ 60%").
 *   - `variant="header"`: slightly larger floating pill for the
 *     "Today across the county" map header, includes the short
 *     forecast text inline + rain probability when ≥20%.
 *
 * Renders nothing when `forecast` is null — never throws, never blocks
 * the rest of the card.
 */

export default function WeatherChip({ forecast, variant = "card" }) {
  if (!forecast || forecast.temp_f == null) return null;
  const isHeader = variant === "header";
  // Show rain chance only when it crosses a threshold worth flagging.
  // Header is more permissive (≥20%) since the user is glancing at the
  // whole region; card is stricter (≥40%) so the chip doesn't get
  // visually busy when 30% pop is showing on every truck in the list.
  const precipThreshold = isHeader ? 20 : 40;
  const showPrecip =
    typeof forecast.precip_pct === "number" &&
    forecast.precip_pct >= precipThreshold;
  return (
    <span
      title={forecast.short || ""}
      data-testid={`weather-chip-${variant}`}
      className={
        isHeader
          ? "inline-flex items-center gap-1.5 rounded-full bg-card/95 backdrop-blur border border-border px-3 py-1.5 text-xs font-semibold shadow-sm"
          : "inline-flex items-center gap-1 rounded-full bg-muted text-foreground/80 px-2 py-0.5 text-[12px] font-semibold tabular-nums"
      }
    >
      <span aria-hidden="true">{forecast.icon || "🌤️"}</span>
      <span>{Math.round(forecast.temp_f)}°</span>
      {isHeader && forecast.short && (
        <span className="text-muted-foreground font-normal">
          · {forecast.short}
        </span>
      )}
      {showPrecip && (
        <span
          className={
            isHeader
              ? "text-sky-600 dark:text-sky-300 font-semibold ml-1"
              : "text-sky-600 dark:text-sky-300 font-semibold ml-0.5"
          }
          data-testid={`weather-chip-${variant}-precip`}
        >
          ☔ {forecast.precip_pct}%
        </span>
      )}
    </span>
  );
}
